import { memo, useCallback } from 'react';
import { Button } from '@aimsos/ui';
import { ArrowLeftRight } from 'lucide-react';
import { useTranslation } from '@aimsos/i18n';

import { DUPLICATE_KIND, DUPLICATE_RESOLUTION } from '../../../constants/inviteModes.js';
import { personDisplayName } from '../../../utils/personDisplayName.js';
import BlockerPerson from './BlockerPerson.jsx';

/**
 * One possible duplicate. Against an existing member: Use existing member / Add as new person.
 * Against another row of the file: Keep row N only / Keep both. The same email twice is one
 * person, so there it is Keep row A only / Keep row B only (no Keep both).
 *
 * @param {object} props
 * @param {object} props.row
 * @param {object} props.issue The `possible_duplicate` issue (`kind` member | file).
 * @param {object|null} props.other The other side: the existing member or the other row's person.
 * @param {(rowNumber: number, resolution: string) => void} props.onResolve
 */
function DuplicateCard({ row, issue, other, onResolve }) {
  const { t } = useTranslation('people-access');
  const isMember = issue.kind === DUPLICATE_KIND.MEMBER;
  const resolve = useCallback((resolution) => () => onResolve(row.rowNumber, resolution), [onResolve, row.rowNumber]);
  const otherPerson = isMember
    ? { first_name: other?.name ?? '', last_name: '', email: other?.email ?? '', job_title: other?.job_title ?? '' }
    : other;

  return (
    <div className="flex flex-col gap-3 border-t border-edge py-3">
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
        <BlockerPerson person={row.person} label={t('import.check.before.inYourFile', { row: row.rowNumber })} />
        <ArrowLeftRight size={16} className="text-content-muted" aria-hidden />
        <BlockerPerson
          person={otherPerson}
          label={
            isMember
              ? t('import.check.before.alreadyMember')
              : t('import.check.before.alsoInFile', { row: issue.otherRowNumber })
          }
        />
      </div>
      {issue.sameEmail ? (
        <p className="m-0 text-xs text-content-secondary">{t('import.check.before.sameEmail')}</p>
      ) : null}
      <div role="group" aria-label={personDisplayName(row.person)} className="flex gap-2">
        {isMember ? (
          <>
            <Button variant="secondary" size="sm" onClick={resolve(DUPLICATE_RESOLUTION.USE_EXISTING)}>
              {t('import.check.before.useExisting')}
            </Button>
            <Button variant="secondary" size="sm" onClick={resolve(DUPLICATE_RESOLUTION.ADD_NEW)}>
              {t('import.check.before.addAsNew')}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" size="sm" onClick={resolve(DUPLICATE_RESOLUTION.KEEP_OTHER)}>
              {t('import.check.before.keepOnly', { row: issue.otherRowNumber })}
            </Button>
            {issue.sameEmail ? (
              <Button variant="secondary" size="sm" onClick={resolve(DUPLICATE_RESOLUTION.KEEP_THIS)}>
                {t('import.check.before.keepOnly', { row: row.rowNumber })}
              </Button>
            ) : (
              <Button variant="secondary" size="sm" onClick={resolve(DUPLICATE_RESOLUTION.KEEP_BOTH)}>
                {t('import.check.before.keepBoth')}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default memo(DuplicateCard);
